'use client';

import { useRef, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { unstable_HistoryRouter as HistoryRouter } from 'react-router-dom';
import {
  createBrowserHistory,
  createMemoryHistory,
  type BrowserHistory,
  type MemoryHistory,
} from '@remix-run/router';

/**
 * SSR-safe router context for legacy `react-router-dom` consumers embedded
 * inside the Next.js App Router.
 *
 * We always render the *same* component (`HistoryRouter`) on the server and
 * the client so React 19 / Next 16 hydration doesn't see two different
 * component types and discard the SSR subtree. The difference between the
 * two environments lives in the `history` prop:
 *
 *   - On the server, we feed `HistoryRouter` a `MemoryHistory` seeded with
 *     Next's pathname so `<Routes>` matches and SSR produces the right HTML.
 *   - On the client, we feed it a real `BrowserHistory` so `useNavigate`,
 *     `useLocation`, and `useParams` track the URL bar — which is what the
 *     rest of the codebase (admin SPA, order flow, simplified-sale share
 *     modal, etc.) has always assumed.
 *
 * The previous approach in this file (always `MemoryRouter`, seeded once from
 * `usePathname()`) avoided the hydration mismatch but left `useNavigate`
 * disconnected from the URL bar. That detachment is what made
 * `window.location.reload()` start misbehaving mid-checkout and forced a
 * pile of band-aid fixes around modals and reload-on-close flows. Using a
 * pre-built history lets us keep the clean hydration *and* the working
 * navigation.
 *
 * `useRef` caches the history instance so we don't re-create it on every
 * render — re-creating browser history would reset internal listeners.
 */
export default function SafeBrowserRouter({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const historyRef = useRef<BrowserHistory | MemoryHistory | null>(null);
  if (!historyRef.current) {
    historyRef.current = typeof window === 'undefined'
      ? createMemoryHistory({ initialEntries: [pathname || '/'] })
      : createBrowserHistory();
  }
  return (
    <HistoryRouter history={historyRef.current}>
      {children}
    </HistoryRouter>
  );
}
